import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";

import { cn } from "@/lib/cn";

export type ToastVariant = "default" | "success" | "warn" | "error";

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /**
   * When true, the toast does not auto-dismiss. Used for rate-limit errors in
   * M7 where the user must acknowledge before continuing.
   */
  sticky?: boolean;
}

interface ActiveToast extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const variantClasses: Record<ToastVariant, string> = {
  default: "",
  success: "border-l-[3px] border-l-[var(--pos)]",
  warn: "border-l-[3px] border-l-[var(--warn)]",
  error: "border-l-[3px] border-l-[var(--neg)]",
};

/**
 * Provider mounts the Radix toast viewport once. Wrap the app root with it
 * (usually inside AppShell or the router). `useToast()` returns a typed
 * enqueue function.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);

  const toast = useCallback((options: ToastOptions) => {
    // Monotonic id — avoids collisions when two toasts fire in the same tick.
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, ...options }]);
  }, []);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider
        swipeDirection="right"
        duration={4000}
      >
        {children}
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            duration={t.sticky ? Infinity : 4000}
            onOpenChange={(open) => {
              if (!open) remove(t.id);
            }}
            className={cn(
              "pointer-events-auto relative grid w-80 gap-1 rounded-[var(--r-md)] border border-[var(--line)] bg-[var(--surface)] py-3 pl-4 pr-10",
              "text-[13px] text-[var(--ink)] shadow-[var(--shadow-2)]",
              "data-[state=open]:animate-overlay-fade-in data-[state=closed]:animate-overlay-fade-out",
              variantClasses[t.variant ?? "default"],
            )}
          >
            <ToastPrimitive.Title className="font-sans text-[13px] font-medium">
              {t.title}
            </ToastPrimitive.Title>
            {t.description && (
              <ToastPrimitive.Description className="text-[13px] text-[var(--ink-3)]">
                {t.description}
              </ToastPrimitive.Description>
            )}
            <ToastPrimitive.Close
              aria-label="Dismiss"
              className={cn(
                "absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-sm",
                "text-[var(--ink-3)] transition-colors duration-fast ease hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
              )}
            >
              <X className="h-4 w-4" strokeWidth={1.5} aria-hidden />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport
          className={cn(
            "fixed bottom-4 right-4 z-[100] flex max-h-screen w-96 flex-col gap-2",
            "outline-none",
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}

// Module-local, frozen no-op fallback. Returning a fresh object per call
// would break referential equality and re-fire any effect that lists
// `toast` in its deps (e.g. <RequireAccountRole>) on every render outside a
// provider — a render-storm tripwire one mount-change away from real harm.
const NOOP_TOAST_VALUE: ToastContextValue = Object.freeze({
  toast: () => {},
});

/**
 * Lenient variant used by features where a missing `<ToastProvider>` should
 * be a no-op rather than a crash — primarily background CSV exports in tests
 * that mount a page without the app shell. If a provider is present you get
 * the real enqueue function; otherwise toast calls become drop-on-floor.
 *
 * The fallback identity is stable across renders so `toast` is safe to use
 * in `useEffect` deps without causing infinite re-runs.
 */
export function useOptionalToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  return ctx ?? NOOP_TOAST_VALUE;
}
